import React, { useState, useEffect, useContext, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/fire";
import { signInWithEmailAndPassword, sendEmailVerification, signOut, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp, addDoc, orderBy, limit, FieldPath, Timestamp } from "firebase/firestore";
import LogoComponent from "./LogoComponent";
import moment from 'moment-timezone';
import { FaArrowAltCircleLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import Modal from "./Modal";
import LoadingOverlay from "./LoadingOverlay";
import { useAuth } from "../context/AuthContext";
import { LogoContext } from "./LogoContext";
import ReCAPTCHA from "react-google-recaptcha";

export default function CoachLogin() {
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [logoUrl, setLogoUrl] = useContext(LogoContext);
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [disclaimerFile, setDisclaimerFile] = useState("");
  const [agreeToDisclaimer, setAgreeToDisclaimer] = useState(false);
  const [formData, setFormData] = useState({
    agreeToDisclaimer: false,
  });
  const [errors, setErrors] = useState({});
  const [barcode, setBarcode] = useState("");
  const [loginMode, setLoginMode] = useState("class");


  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [newAgreement, setNewAgreement] = useState(null);
  const [oldAgreement, setOldAgreement] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const formStartTime = useRef(Date.now());
  const interactionCount = useRef(0);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const trackInteraction = () => {
    interactionCount.current += 1;
  };

  const onRecaptchaChange = (token) => {
    trackInteraction();
    setRecaptchaToken(token);
  };

  const onRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };



  const openPdfInNewTab = (pdfBase64, title = "PDF Document") => {
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="margin:0">
          <embed src="${pdfBase64}" type="application/pdf" width="100%" height="100%" />
        </body>
      </html>
    `);
      newWindow.document.close();
    } else {
      alert("Popup blocked! Please allow popups for this website.");
    }
  };

  const handleAcceptNewAgreement = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      await updateDoc(doc(db, "coaches", user.uid), {
        signedAgreement: newAgreement.fileData,
        agreementDate: Timestamp.now(),
        // agreementDate: new Date(),
      });
      setShowAgreementModal(false);
      setLoading(false);
      await continueLogin(pendingLoginData);
    } catch (error) {
      console.error("Error accepting agreement:", error);
      setLoading(false);
    }
  };

  const handleSkipAgreement = async () => {
    setShowAgreementModal(false);
    await continueLogin(pendingLoginData);
  };

  const continueLogin = async ({ coachData, ongoingClass, upcomingClass, todayDate, now }) => {
    try {
      if (!ongoingClass && !upcomingClass) {
        showModal("No ongoing or upcoming classes found within the next 30 minutes.");
        resetForm();
        return;
      }

      const alreadyLoggedInOngoing = ongoingClass
        ? await checkExistingWorklog(coachData.name, ongoingClass.id, todayDate)
        : false;

      const alreadyLoggedInUpcoming = !ongoingClass && upcomingClass
        ? await checkExistingWorklog(coachData.name, upcomingClass.id, todayDate)
        : false;

      if (!alreadyLoggedInOngoing && ongoingClass) {
        await logWork(coachData, ongoingClass, todayDate, now);
        return;
      }

      if (!alreadyLoggedInUpcoming && upcomingClass) {
        await logWork(coachData, upcomingClass, todayDate, now);
        return;
      }

      if (alreadyLoggedInOngoing) {
        showModal(`You've already logged in for ${ongoingClass.className}.`);
      } else if (alreadyLoggedInUpcoming) {
        showModal(`You've already logged in for ${upcomingClass.className}.`);
      }

      resetForm();
    } catch (error) {
      console.error("Error completing coach login:", error);
      showModal("Something went wrong finishing your login.");
      setLoading(false);
      resetForm();
    }
  };

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validatePhone = (phone) => {
    const re = /^[0-9]{8,15}$/;
    return re.test(phone);
  };

  useEffect(() => {
    const fetchDisclaimer = async () => {
      try {
        const docRef = doc(db, "agreements", "instructor");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setDisclaimerFile({
            name: data.fileName,
            type: data.fileType,
            content: data.fileData,
          });
        } else {
          console.error("No disclaimer found in database.");
        }
      } catch (error) {
        console.error("Error fetching disclaimer: ", error);
      }
    };

    fetchDisclaimer();
  }, []);

  const openDisclaimerModal = () => {
    trackInteraction();
    setIsDisclaimerModalOpen(true);
  };

  const closeDisclaimerModal = () => {
    setIsDisclaimerModalOpen(false);
  };

  const acceptDisclaimer = () => {
    trackInteraction();
    setFormData({ ...formData, agreeToDisclaimer: true });
    closeDisclaimerModal();
  };

  const declineDisclaimer = () => {
    trackInteraction();
    setFormData({ ...formData, agreeToDisclaimer: false });
    closeDisclaimerModal();
  };

  const checkUserGeolocation = async () => {
    try {
      const locationRef = doc(db, "settings", "location");
      const locationSnap = await getDoc(locationRef);

      if (!locationSnap.exists()) {
        return false;
      }

      const gymLocation = locationSnap.data();

      if (navigator.geolocation) {
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const userLatitude = position.coords.latitude;
              const userLongitude = position.coords.longitude;

              const isWithinGym = checkIfWithinGym(userLatitude, userLongitude, gymLocation);
              resolve(isWithinGym);
            },
            (error) => {
              console.error("Error getting location: ", error);
              resolve(false);
            }
          );
        });
      } else {

        return false;
      }
    } catch (error) {
      console.error("Error checking location: ", error);
      return false;
    }
  };

  const checkIfWithinGym = (userLat, userLng, gymLocation) => {

    const distance = getDistanceFromLatLonInKm(userLat, userLng, gymLocation.latitude, gymLocation.longitude);
    return distance <= 0.1;  // Within 100 meters
  };

  const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const deg2rad = (deg) => deg * (Math.PI / 180);

  const handleUnifiedLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let loginEmail = email;

      // CLASS MODE → barcode lookup
      if (loginMode === "class") {
        if (!barcode || !password) {
          showModal("Please scan your barcode and enter your password.");
          resetForm();
          return;
        }

        loginEmail = await getEmailByBarcode(barcode);
        if (!loginEmail) return;
      }

      // DASHBOARD MODE → email login
      if (loginMode === "dashboard") {
        if (!email || !password) {
          showModal("Please enter your email and password.");
          setLoading(false);
          return;
        }
      }

      // AUTH
      const user = await authenticateUser(loginEmail, password);
      if (!user) return;

      // COACH CHECK
      const coachData = await getCoachData(user.uid);
      if (!coachData) return;

      // AGREEMENT CHECK
      const agreementDoc = await getDoc(doc(db, "agreements", "instructor"));
      const latestAgreement = agreementDoc.data();
      const coachAgreementDate = coachData.agreementDate?.toDate();
      const latestAgreementDate = latestAgreement.timestamp?.toDate();

      if (!coachAgreementDate || coachAgreementDate < latestAgreementDate) {
        setOldAgreement(coachData.signedAgreement);
        setNewAgreement(latestAgreement);
        setPendingLoginData({ coachData });
        setShowAgreementModal(true);
        setLoading(false);
        return;
      }

      // DASHBOARD ACCESS
      if (loginMode === "dashboard") {
        setLoading(false);
        navigate("/coach-dashboard", { state: { user: user.uid } });
        return;
      }

      // CLASS CHECK-IN
      const { ongoingClass, upcomingClass, todayDate, now } =
        await getClassInfo(coachData.name);

      await continueLogin({
        coachData,
        ongoingClass,
        upcomingClass,
        todayDate,
        now,
      });

    } catch (error) {
      console.error("Login error:", error);
      showModal("Login failed. Please check your details.");
    } finally {
      setLoading(false);
    }
  };


  const handleSignUp = async (e) => {
    setErrors('');
    e.preventDefault();
    setError("");
    // 1. Honeypot
    if (formData.honeypot) {
      showModal("Registration failed. Please try again.");
      return;
    }

    // 2. Time check (seconds)
    const timeTaken = (Date.now() - formStartTime.current) / 1000;
    if (timeTaken < 5) {
      showModal("Please take your time filling out the form.");
      return;
    }

    // 3. Interaction check
    if (interactionCount.current < 5) {
      showModal("Please complete all form fields.");
      return;
    }

    // 4. reCAPTCHA
    if (!recaptchaToken) {
      showModal("Please verify that you're not a robot.");
      return;
    }

    if (!name || !email || !password || !phone) {
      alert("All fields are required.");
      return;
    }

    if (!validateEmail(email)) {
      alert("Invalid email format.");
      return;
    }

    if (!validatePhone(phone)) {
      alert("Invalid phone number.");
      return;
    }


    if (!formData.agreeToDisclaimer) {
      setErrors({ ...errors, disclaimer: "Please accept the disclaimer before signing in" });
      return;
    }

    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      setAgreeToDisclaimer(formData.agreeToDisclaimer);

      await sendEmailVerification(user);
      await setDoc(doc(db, "coaches", user.uid), {
        name,
        email,
        phone,
        isCoach: false,
        isActiveCoach: false,
        agreeToDisclaimer: true,
        signedAgreement: disclaimerFile.content,
        agreementDate: Timestamp.now(),
        recaptchaVerified: true,
        registrationDate: Timestamp.now(),
      });
      clearSignupForm();
      showModal(
        <div className="text-sm">
          <div className="mb-4 pb-3 border-b border-gray-200">
            <div className="flex items-center gap-2 text-green-600 font-medium">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
              Sign-up successful!
            </div>
          </div>

          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-3 py-2">
              <div className="font-semibold text-gray-900">Verify your email</div>
              <div className="text-gray-600 text-xs mt-1">
                Check your spam/junk folder if you don't see the verification email
              </div>
            </div>

            <div className="border-l-4 border-amber-500 pl-3 py-2">
              <div className="font-semibold text-gray-900">Admin approval pending</div>
              <div className="text-gray-600 text-xs mt-1">
                Your registration is awaiting approval
              </div>
            </div>
          </div>
        </div>
      );
      // showModal("Sign-up successful! Please verify your email and wait for admin approval.");
      await signOut(auth);
      interactionCount.current = 0;
      formStartTime.current = Date.now();

    } catch (error) {
      if (error.code === "auth/email-already-in-use" ||
        (error.error && error.error.message === "EMAIL_EXISTS")) {
        showModal("This email is already registered. Please log in instead.");
      } else {
        showModal(error.message || "An error occurred during sign up. Please try again.");
      }
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);

    } finally {
      setLoading(false);
      setErrors('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Validate input
    if (!barcode || !password) {
      showModal("Please scan your barcode and provide your password.");
      resetForm();
      return;
    }

    try {
      // Lookup coach by barcode
      const loginEmail = await getEmailByBarcode(barcode);
      if (!loginEmail) return;

      // Authenticate user
      const user = await authenticateUser(loginEmail, password);
      if (!user) return;

      // Check coach document and status
      const coachData = await getCoachData(user.uid);
      if (!coachData) return;

      // Find ongoing/upcoming class
      const { ongoingClass, upcomingClass, todayDate, now } = await getClassInfo(coachData.name);

      const agreementDoc = await getDoc(doc(db, "agreements", "instructor"));
      const latestAgreement = agreementDoc.data();

      const coachDocRef = doc(db, "coaches", user.uid);
      const coachDocSnap = await getDoc(coachDocRef);
      const coachDataAgreement = coachDocSnap.data();

      const coachAgreementDate = coachDataAgreement.agreementDate?.toDate();
      const latestAgreementDate = latestAgreement.timestamp?.toDate();

      if (!coachAgreementDate || coachAgreementDate < latestAgreementDate) {
        setOldAgreement(coachDataAgreement.signedAgreement);
        setNewAgreement(latestAgreement);
        setPendingLoginData({
          coachData,
          ongoingClass,
          upcomingClass,
          todayDate,
          now,
        });
        setShowAgreementModal(true);
        setLoading(false);
        return;
      }


      if (!ongoingClass && !upcomingClass) {
        showModal("No ongoing or upcoming classes found within the next 30 minutes.");
        resetForm();
        return;
      }

      // Check if already logged in
      const alreadyLoggedInOngoing = ongoingClass
        ? await checkExistingWorklog(coachData.name, ongoingClass.id, todayDate)
        : false;
      const alreadyLoggedInUpcoming = !ongoingClass && upcomingClass
        ? await checkExistingWorklog(coachData.name, upcomingClass.id, todayDate)
        : false;

      // Handle worklog entry
      if (!alreadyLoggedInOngoing && ongoingClass) {
        await logWork(coachData, ongoingClass, todayDate, now);
        return;
      }
      if (!alreadyLoggedInUpcoming && upcomingClass) {
        await logWork(coachData, upcomingClass, todayDate, now);
        return;
      }


      // Already logged in
      if (alreadyLoggedInOngoing) {
        showModal(`You've already logged in for ${ongoingClass.className}.`);
      } else if (alreadyLoggedInUpcoming) {
        showModal(`You've already logged in for ${upcomingClass.className}.`);
      }
      resetForm();
    } catch (error) {
      console.error("Login error:", error);
      showModal("The barcode, email, or password is incorrect. Please enter correct details.");
      resetForm();
    }
  };


  const resetForm = () => {
    setLoading(false);
    setBarcode("");
  };

  const clearSignupForm = () => {
    setName("");
    setEmail("");
    setPassword("");
    setPhone("");
    setFormData({ agreeToDisclaimer: false });
    setErrors({});
    setAgreeToDisclaimer(false);
  };

  const getEmailByBarcode = async (barcode) => {
    try {
      const coachesRef = collection(db, "coaches");
      const q = query(coachesRef, where("barcode", "==", barcode));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        showModal("No instructor found for this barcode.");
        resetForm();
        return null;
      }
      return snapshot.docs[0].data().email;
    } catch (err) {
      showModal("Error looking up instructor by barcode.");
      resetForm();
      return null;
    }
  };

  const authenticateUser = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(user);
        await signOut(auth);
        showModal("Please verify your email before logging in. A new verification email has been sent.");
        resetForm();
        return null;
      }
      return user;
    } catch (err) {
      showModal("Authentication failed. Please check your credentials.");
      resetForm();
      return null;
    }
  };

  const getCoachData = async (uid) => {
    try {
      const coachDoc = await getDoc(doc(db, "coaches", uid));
      if (!coachDoc.exists()) {
        await signOut(auth);
        showModal("Access Denied. You are not registered as a coach.");
        resetForm();
        return null;
      }
      const coachData = coachDoc.data();
      if (!coachData.isCoach || !coachData.isActiveCoach) {
        await signOut(auth);
        showModal("Your Instructor account is not yet approved. Please contact the admin.");
        resetForm();
        return null;
      }
      return coachData;
    } catch (err) {
      showModal("Error retrieving coach data.");
      resetForm();
      return null;
    }
  };

  const getClassInfo = async (coachName) => {
    const now = moment().tz('Pacific/Auckland');
    const todayDate = now.format('YYYY-MM-DD');
    const ongoingClass = await findOngoingClass(coachName);
    const upcomingClass = await findUpcomingClass(coachName);
    return { ongoingClass, upcomingClass, todayDate, now };
  };

  const logWork = async (coachData, classInfo, todayDate, now) => {
    const loginTime = Timestamp.now();
    const welcomeMessage = `Welcome ${coachData.name}, have a good session`;
    speakWelcomeMessage(welcomeMessage);

    await handleWorklogEntry(
      coachData.name,
      classInfo.id,
      classInfo.className,
      loginTime,
      classInfo.startTime,
      classInfo.endTime,
      todayDate,
      classInfo.isSpecialClass
    );

    const currentTime = now.format('HH:mm:ss');
    showModal(`Logged in for ${classInfo.className} at ${currentTime}`);
    resetForm();
  };


  const speakWelcomeMessage = (message) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(message);
      speechSynthesis.speak(utterance);
    }
  };

  const checkExistingWorklog = async (coachName, classId, date) => {
    setLoading(true);
    try {
      const worklogRef = doc(db, "worklog", date);
      const worklogDoc = await getDoc(worklogRef);

      if (worklogDoc.exists()) {
        const worklogData = worklogDoc.data();
        return !!(worklogData[coachName] && worklogData[coachName][classId] && worklogData[coachName][classId].length > 0);
      }

      return false;
    } catch (error) {
      console.error("Error checking existing worklog:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleWorklogEntry = async (coachName, classId, className, loginTime, scheduledStart, scheduledEnd, todayDate, isSpecialClass) => {
    const worklogRef = doc(db, "worklog", todayDate);
    const worklogDoc = await getDoc(worklogRef);

    let worklogData = worklogDoc.exists() ? worklogDoc.data() : {};

    if (!worklogData[coachName]) {
      worklogData[coachName] = {};
    }

    if (!worklogData[coachName][classId]) {
      worklogData[coachName][classId] = [];
    }

    const entry = {
      className,
      loginTime,
      scheduledStart,
      scheduledEnd,
      isSpecialClass
    };

    Object.keys(entry).forEach(key => entry[key] === undefined && delete entry[key]);

    worklogData[coachName][classId].push(entry);

    await setDoc(worklogRef, worklogData, { merge: true });
  };


  const handleDashboardAccess = async () => {

    if (!(email) || !password) {
      showModal("Please enter an email and provide your password.");
      setLoading(false);
      return;
    }

    let loginEmail = email ? email : null;
    if (!loginEmail && barcode) {
      try {
        const coachesRef = collection(db, "coaches");
        const q = query(coachesRef, where("barcode", "==", barcode));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          showModal("No instructor found for this barcode.");
          setLoading(false);
          setBarcode("");
          return;
        }

        const coachDoc = snapshot.docs[0];
        const coachData = coachDoc.data();
        loginEmail = coachData.email;
      } catch (err) {
        showModal("Error looking up instructor by barcode.");
        setLoading(false);
        setBarcode("");
        return;
      }
    }

    console.log(loginEmail, password, auth)
    try {
      setLoading(true);
      const userCredential = await signInWithEmailAndPassword(auth, loginEmail, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        await sendEmailVerification(user);
        await signOut(auth);
        showModal("Please verify your email before accessing the dashboard. A new verification email has been sent.");
        setLoading(false);
        return;
      }

      const coachDoc = await getDoc(doc(db, "coaches", user.uid));
      if (!coachDoc.exists()) {
        await signOut(auth);
        showModal("Access Denied. You are not registered as a coach.");
        setLoading(false);
        return;
      }

      const agreementDoc = await getDoc(doc(db, "agreements", "instructor"));
      const latestAgreement = agreementDoc.data();

      const coachDocRef = doc(db, "coaches", user.uid);
      const coachDocSnap = await getDoc(coachDocRef);
      const coachData = coachDocSnap.data();

      const coachAgreementDate = coachData.agreementDate?.toDate();
      const latestAgreementDate = latestAgreement.timestamp?.toDate();

      if (!coachAgreementDate || coachAgreementDate < latestAgreementDate) {
        setOldAgreement(coachData.signedAgreement);
        setNewAgreement(latestAgreement);
        setShowAgreementModal(true);
        setLoading(false);
        return;
      }

      console.log(user.uid)
      setLoading(false);
      navigate("/coach-dashboard", { state: { user: user.uid } });


    } catch (error) {
      setLoading(false);
      console.error("Dashboard access error:", error);
      showModal("Access failed. Please check your credentials and try again.");
    }
  };

  const handleSpecialClassWorklog = async (coachName, classId, className, loginTime, scheduledStart, scheduledEnd, todayDate) => {
    const worklogRef = doc(db, "worklog", todayDate);
    const worklogDoc = await getDoc(worklogRef);

    let worklogData = worklogDoc.exists() ? worklogDoc.data() : {};

    if (!worklogData[coachName]) {
      worklogData[coachName] = {};
    }

    if (!worklogData[coachName][classId]) {
      worklogData[coachName][classId] = [];
    }

    worklogData[coachName][classId].push({
      className,
      loginTime,
      scheduledStart,
      scheduledEnd,
      isSpecialClass: true
    });

    await setDoc(worklogRef, worklogData, { merge: true });
  };

  const findOngoingClass = async (coachName) => {
    const now = moment().tz('Pacific/Auckland');
    const today = now.format('YYYY-MM-DD');
    const classScheduleRef = collection(db, "classSchedule");
    const q = query(classScheduleRef, where("coaches", "array-contains", coachName));
    const querySnapshot = await getDocs(q);

    let ongoingClass = null;
    querySnapshot.forEach((doc) => {
      const classData = doc.data();
      const classStartTime = moment(classData.startTime, 'HH:mm');
      const classEndTime = moment(classData.endTime, 'HH:mm');

      if (classData.isSpecialClass && classData.specialClassDate === today) {
        if (now.isBetween(classStartTime, classEndTime)) {
          ongoingClass = { ...classData, id: doc.id };
        }
      } else if (!classData.isSpecialClass && classData.days.includes(now.format('dddd'))) {
        if (now.isBetween(classStartTime, classEndTime)) {
          ongoingClass = { ...classData, id: doc.id };
        }
      }
    });

    return ongoingClass;
  };

  const findUpcomingClass = async (coachName) => {
    const now = moment().tz('Pacific/Auckland');
    const today = now.format('YYYY-MM-DD');
    const classScheduleRef = collection(db, "classSchedule");
    const q = query(classScheduleRef, where("coaches", "array-contains", coachName));
    const querySnapshot = await getDocs(q);

    let upcomingClass = null;
    querySnapshot.forEach((doc) => {
      const classData = doc.data();
      const classStartTime = moment(classData.startTime, 'HH:mm');
      const classEndTime = moment(classData.endTime, 'HH:mm');

      if (classData.isSpecialClass && classData.specialClassDate === today) {
        if (classStartTime.isAfter(now) && classStartTime.diff(now, 'minutes') <= 30) {
          upcomingClass = { ...classData, id: doc.id };
        } else if (now.isBetween(classStartTime, classEndTime)) {
          upcomingClass = { ...classData, id: doc.id };
        }
      } else if (!classData.isSpecialClass && classData.days.includes(now.format('dddd'))) {
        if (classStartTime.isAfter(now) && classStartTime.diff(now, 'minutes') <= 30) {
          upcomingClass = { ...classData, id: doc.id };
        } else if (now.isBetween(classStartTime, classEndTime)) {
          upcomingClass = { ...classData, id: doc.id };
        }
      }
    });

    return upcomingClass;
  };

  return (
    <LoadingOverlay isLoading={loading}>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Top bar */}
        <div className="px-4 sm:px-8 pt-5 pb-2">
          <Link to="/" className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-900 text-sm font-semibold transition-colors">
            <FaArrowAltCircleLeft size={18} />
            Back
          </Link>
        </div>

        {/* Main content */}
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
          {/* Logo */}
          <img
            src={logoUrl}
            alt="Gym logo"
            className="h-24 sm:h-32 object-contain mb-6"
          />

          {/* Card */}
          <div className="w-full max-w-sm sm:max-w-md bg-white rounded-2xl shadow-md border border-gray-100 px-6 py-8 sm:px-10 sm:py-10">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-1 text-center">
              Instructor Portal
            </h1>
            <p className="text-sm text-gray-400 text-center mb-6">
              {tab === "login" ? "Sign in to check in or view your dashboard" : "Request access as an instructor"}
            </p>

            {/* Tab switcher */}
            <div className="flex rounded-lg overflow-hidden border border-gray-200 mb-7">
              <button
                type="button"
                onClick={() => setTab("login")}
                className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${tab === "login" ? "bg-purple-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => setTab("signup")}
                className={`flex-1 py-2.5 text-sm font-semibold transition-colors border-l border-gray-200 ${tab === "signup" ? "bg-purple-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
              >
                Sign Up
              </button>
            </div>

            {tab === "login" && (
              <form className="flex flex-col gap-4" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false">
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                <div className="flex gap-2 p-3 bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-lg text-xs">
                  <span className="font-bold shrink-0">Reminder:</span>
                  <span>Instructors must be present at the gym when checking in for a class.</span>
                </div>

                {/* Mode selector */}
                <div className="flex flex-col gap-1 mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">What would you like to do?</label>
                  <div className="flex flex-col gap-2">
                    <label className={`flex items-start gap-3 p-3.5 border rounded-lg cursor-pointer transition-colors ${loginMode === "class" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"}`}>
                      <input
                        type="radio"
                        checked={loginMode === "class"}
                        onChange={() => setLoginMode("class")}
                        className="mt-0.5 accent-purple-600"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Class Check-In</div>
                        <div className="text-xs text-gray-400 mt-0.5">On-site only · Barcode required</div>
                      </div>
                    </label>
                    <label className={`flex items-start gap-3 p-3.5 border rounded-lg cursor-pointer transition-colors ${loginMode === "dashboard" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-purple-300"}`}>
                      <input
                        type="radio"
                        checked={loginMode === "dashboard"}
                        onChange={() => setLoginMode("dashboard")}
                        className="mt-0.5 accent-purple-600"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Coach Dashboard</div>
                        <div className="text-xs text-gray-400 mt-0.5">Access schedules, reports, and settings from anywhere</div>
                      </div>
                    </label>
                  </div>
                </div>

                {loginMode === "class" && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Barcode</label>
                    <input
                      type="password"
                      value={barcode}
                      onChange={e => setBarcode(e.target.value)}
                      placeholder="Scan your barcode"
                      autoComplete="off"
                      autoFocus={loginMode === "class"}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                    />
                    {barcode && <p className="text-green-600 text-xs font-medium mt-0.5">Barcode scanned</p>}
                  </div>
                )}

                {loginMode === "dashboard" && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Address</label>
                    <input
                      type="email"
                      placeholder="coach@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                    />
                  </div>
                )}

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Password</label>
                  <div className="relative flex items-center">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      required
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      spellCheck="false"
                      className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                    />
                    {password && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                        tabIndex="-1"
                      >
                        {showPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex justify-end -mt-1">
                  <Link to="/forgot-password" className="text-xs text-purple-600 hover:text-purple-800 hover:underline font-medium">
                    Forgot Password?
                  </Link>
                </div>

                <button
                  type="button"
                  onClick={handleUnifiedLogin}
                  disabled={!loginMode}
                  className="w-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:bg-purple-300 text-white font-semibold py-3 rounded-lg shadow-sm transition-colors duration-200 text-sm"
                >
                  {loginMode === "class" ? "Check In to Class" : loginMode === "dashboard" ? "Access Dashboard" : "Continue"}
                </button>
              </form>
            )}

            {tab === "signup" && (
              <form onSubmit={handleSignUp} className="flex flex-col gap-4" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false">
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Sam Wilson"
                    value={name}
                    onChange={(e) => { trackInteraction(); setName(e.target.value); }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone Number</label>
                  <input
                    type="tel"
                    placeholder="e.g. 021 234 5678"
                    value={phone}
                    onChange={(e) => { trackInteraction(); setPhone(e.target.value); }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Address</label>
                  <input
                    type="email"
                    placeholder="coach@example.com"
                    value={email}
                    onChange={(e) => { trackInteraction(); setEmail(e.target.value); }}
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Password</label>
                  <div className="relative flex items-center">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => { trackInteraction(); setPassword(e.target.value); }}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      spellCheck="false"
                      className="w-full px-4 py-3 pr-11 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition"
                    />
                    {password && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                        tabIndex="-1"
                      >
                        {showPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
                      </button>
                    )}
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="flex flex-col gap-2 p-3.5 border border-gray-200 rounded-lg bg-gray-50">
                  <button
                    type="button"
                    onClick={openDisclaimerModal}
                    className="text-xs text-purple-600 hover:text-purple-800 font-semibold underline text-left w-fit"
                  >
                    Read Disclaimer
                  </button>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="agreeToDisclaimer"
                      checked={formData.agreeToDisclaimer}
                      onChange={(e) => setFormData({ ...formData, agreeToDisclaimer: e.target.checked })}
                      className="mt-0.5 accent-purple-600"
                    />
                    <span className="text-xs text-gray-600">I have read and agree to the disclaimer</span>
                  </label>
                  {errors.disclaimer && <p className="text-red-500 text-xs">{errors.disclaimer}</p>}
                </div>

                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={onRecaptchaChange}
                    onExpired={onRecaptchaExpired}
                    theme="light"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg shadow-sm transition-colors duration-200 text-sm"
                >
                  {loading ? "Processing…" : "Request Access"}
                </button>
              </form>
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
          <p>{modalMessage}</p>
        </Modal>

        {/* Disclaimer modal */}
        {isDisclaimerModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white p-6 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <h3 className="text-lg font-bold mb-4 text-gray-800">{disclaimerFile.name || "Disclaimer"}</h3>
              <div className="relative w-full h-96 rounded-lg overflow-hidden border border-gray-200">
                <embed src={`${disclaimerFile.content}`} type={disclaimerFile.type} width="100%" height="100%" />
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={declineDisclaimer}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  Decline
                </button>
                <button
                  onClick={acceptDisclaimer}
                  className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Agreement update modal */}
        {showAgreementModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl shadow-xl max-w-2xl w-full overflow-y-auto max-h-[90vh]">
              <h2 className="text-lg font-bold mb-1 text-gray-800 text-center">Updated Terms and Conditions</h2>
              <p className="text-sm text-gray-500 text-center mb-6">
                Our Terms and Conditions have changed since your last login.
              </p>

              <div className="mb-5 p-4 border border-gray-100 rounded-xl bg-gray-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Previously accepted</p>
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={true} disabled className="accent-purple-600" />
                  <label className="text-sm text-gray-700">Our previous Terms and Conditions</label>
                </div>
                {oldAgreement ? (
                  <button
                    onClick={() => openPdfInNewTab(oldAgreement, "Previously Accepted Agreement")}
                    className="text-xs text-purple-600 hover:text-purple-800 underline font-medium"
                  >
                    View previously accepted agreement (PDF)
                  </button>
                ) : (
                  <p className="text-xs text-red-500">Previous agreement not available.</p>
                )}
              </div>

              <div className="mb-6 p-4 border border-purple-100 rounded-xl bg-purple-50">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Action required</p>
                {newAgreement?.fileData ? (
                  <button
                    onClick={() => openPdfInNewTab(newAgreement.fileData, "Updated Terms and Conditions")}
                    className="text-xs text-purple-600 hover:text-purple-800 underline font-medium mb-3 block"
                  >
                    View updated Terms and Conditions (PDF)
                  </button>
                ) : (
                  <p className="text-xs text-red-500 mb-3">Updated agreement not available.</p>
                )}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="agreementCheck"
                    checked={agreementAccepted}
                    onChange={(e) => setAgreementAccepted(e.target.checked)}
                    className="mt-0.5 accent-purple-600"
                  />
                  <span className="text-sm text-gray-700">I have read and accept the updated Terms and Conditions</span>
                </label>
              </div>

              <div className="flex justify-between gap-3">
                <button
                  onClick={handleSkipAgreement}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={handleAcceptNewAgreement}
                  disabled={!agreementAccepted}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg text-white transition-colors ${agreementAccepted ? "bg-purple-600 hover:bg-purple-700" : "bg-purple-300 cursor-not-allowed"}`}
                >
                  Accept & Continue
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LoadingOverlay>
  );
}